import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login, admin, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back 🐒");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-cream px-6 py-16" data-testid="admin-login">
      <div className="w-full max-w-md rounded-3xl border-[3px] border-brand-ink bg-white p-8 shadow-brutal">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-brand-cream rounded-2xl p-1 border-[3px] border-brand-ink">
            <Logo size={44} />
          </div>
          <div>
            <p className="font-display text-2xl font-semibold text-brand-red leading-none">Cheeky Monkeys</p>
            <p className="text-sm text-brand-ink/60">Admin dashboard</p>
          </div>
        </div>

        <h1 className="font-display text-3xl font-bold text-brand-ink">Welcome back</h1>
        <p className="text-brand-ink/70 mt-1">Sign in to manage enrollments, testimonials & gallery.</p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <label className="block">
            <span className="text-sm font-bold text-brand-ink">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              data-testid="admin-login-email"
              className="mt-1 w-full h-12 rounded-xl border-2 border-brand-ink bg-white px-3 focus:outline-none focus:ring-2 focus:ring-brand-red"
            />
          </label>
          <label className="block">
            <span className="text-sm font-bold text-brand-ink">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="admin-login-password"
              className="mt-1 w-full h-12 rounded-xl border-2 border-brand-ink bg-white px-3 focus:outline-none focus:ring-2 focus:ring-brand-red"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-red text-white font-bold h-12 btn-brutal"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
